/**
 * What the ROI page counts (client notes 10/26, ROI 1): the threats the console handled in a period of 7 days, 30 days or a
 * year. A year is not counted, it is twelve 30-day values, as the notes say; a period with nothing in it is "unavailable"
 * and is never shown as a zero. Pure, so the rules are tested without a screen.
 */
import { isWithinFunnelWindow, normalizeRiskBand, remediationTime, sourceRefId, type FunnelWindow } from './funnel';

export type RoiPeriod = '7d' | '30d' | '1y';
export const ROI_PERIODS: readonly RoiPeriod[] = ['7d', '30d', '1y'];
export const DEFAULT_ROI_PERIOD: RoiPeriod = '30d';

export const ROI_PERIOD_WORDS: Record<RoiPeriod, string> = { '7d': '7 days', '30d': '30 days', '1y': '1 year' };

/** Where each period is counted. A year is twelve 30-day values, so it reads the 30 days. */
const COUNTED_IN: Record<RoiPeriod, FunnelWindow> = { '7d': '7d', '30d': '1m', '1y': '1m' };
const YEAR_FACTOR = 12;
const DAYS: Record<RoiPeriod, number> = { '7d': 7, '30d': 30, '1y': 30 * YEAR_FACTOR };

export type RoiSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface RoiCounts {
  /** Remediations that finished in the period: the work a person did not have to do by hand. */
  remediated: number;
  /** Threats that came in during the period, by severity. */
  bySeverity: Record<RoiSeverity, number>;
  /** Threats Managed: the sum of the four severities. */
  managed: number;
  /**
   * The mean time to remediate, in minutes, measured on the remediations that finished in the period: from the first alert of
   * the threat to the check that closed it. Null when none of them has both times. A mean does not grow with the period.
   */
  meanMinutes: number | null;
}

interface RoiThreat {
  _id?: string;
  created_at?: string;
  first_event_time?: string;
  detection_risk_level?: string;
}

interface RoiRemediation {
  status?: string;
  threat_event_id?: unknown;
  verified_at?: string;
  decided_at?: string;
  assigned_at?: string;
  formed_at?: string;
}

/** The number of weeks a period is, which is what the capacity of a team is measured against. */
export function roiWeeks(period: RoiPeriod): number {
  return DAYS[period] / 7;
}

/** How many times the 30-day count the period is: 12 for a year, 1 otherwise. */
export function roiMultiplier(period: RoiPeriod): number {
  return period === '1y' ? YEAR_FACTOR : 1;
}

/**
 * The counts of a period, or null when there is not enough data in it: nothing came in and nothing finished. The year
 * is the 30-day count times 12, never a count of its own.
 */
export function roiCounts(
  inputs: { threatEvents: readonly RoiThreat[]; remediations: readonly RoiRemediation[] },
  nowMs: number,
  period: RoiPeriod
): RoiCounts | null {
  const window = COUNTED_IN[period];
  const bySeverity: Record<RoiSeverity, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const threat of inputs.threatEvents) {
    if (!isWithinFunnelWindow(threat.created_at || threat.first_event_time, nowMs, window)) continue;
    bySeverity[normalizeRiskBand(threat.detection_risk_level)] += 1;
  }
  const finished = inputs.remediations.filter(
    (rem) =>
      String(rem.status || '').toLowerCase() === 'completed' && isWithinFunnelWindow(remediationTime(rem), nowMs, window)
  );
  const remediated = finished.length;
  const managed = bySeverity.critical + bySeverity.high + bySeverity.medium + bySeverity.low;
  if (managed === 0 && remediated === 0) return null;

  const threatById = new Map(inputs.threatEvents.map((threat) => [threat._id, threat]));
  const minutes: number[] = [];
  for (const rem of finished) {
    const threat = threatById.get(sourceRefId(rem.threat_event_id));
    const start = Date.parse(threat?.first_event_time || threat?.created_at || rem.formed_at || '');
    const end = Date.parse(rem.verified_at || '');
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) minutes.push((end - start) / 60_000);
  }
  const meanMinutes = minutes.length > 0 ? minutes.reduce((sum, value) => sum + value, 0) / minutes.length : null;

  const k = roiMultiplier(period);
  return {
    remediated: remediated * k,
    meanMinutes,
    bySeverity: {
      critical: bySeverity.critical * k,
      high: bySeverity.high * k,
      medium: bySeverity.medium * k,
      low: bySeverity.low * k,
    },
    managed: managed * k,
  };
}

/** The working of MTTR Efficiency (client notes 10/26, ROI 3), in minutes. */
export interface MttrEfficiency {
  /** Threats x the mean time to remediate by hand. */
  preMinutes: number;
  /** Threats x the mean time to remediate with Arbitium, measured. */
  postMinutes: number;
  /** The share of the time that is no longer spent; negative when remediating takes longer than by hand. */
  gainPct: number;
}

/**
 * MTTR Efficiency. The notes give: Pre-Total = threats x average MTTR before Arbitium (the Values box's Mean Time to
 * Remediate), Post-Total = threats x average MTTR with Arbitium (measured on the remediations of the period), and
 * Efficiency Gains = Post / Pre x %. That quotient is the share of the time still spent, which falls as Arbitium gets
 * better, so a gain read from it would be backwards. The gain is what is no longer spent: (Pre - Post) / Pre.
 * Null when there is nothing to compare: no remediation in the period, no measured time, or no time by hand.
 */
export function mttrEfficiency(counts: RoiCounts, manualMinutes: number): MttrEfficiency | null {
  if (counts.meanMinutes == null || counts.remediated <= 0 || !(manualMinutes > 0)) return null;
  const preMinutes = counts.remediated * manualMinutes;
  const postMinutes = counts.remediated * counts.meanMinutes;
  return { preMinutes, postMinutes, gainPct: ((preMinutes - postMinutes) / preMinutes) * 100 };
}
